import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import DoctorCard from "@/components/DoctorCard";
import { useSettings } from "@/context/SettingsContext";
import {
  Search, MapPin, Stethoscope, Heart, Baby, Smile, Sparkles, Bone, Ear, Eye, Brain, Activity,
  ShieldCheck, Timer, BadgeCheck, ArrowRight, MessageSquare, PhoneCall,
} from "lucide-react";

const SPEC_ICON = {
  "general-physician": Stethoscope,
  "dentist": Smile,
  "dermatologist": Sparkles,
  "cardiologist": Heart,
  "gynecologist": Baby,
  "pediatrician": Baby,
  "orthopedic": Bone,
  "ent": Ear,
  "ophthalmologist": Eye,
  "neurologist": Brain,
};

export default function HomePage() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [areas, setAreas] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/cities/pune/areas").then((r) => setAreas(r.data.items || []));
    api.get("/specialties").then((r) => setSpecialties(r.data.items || []));
    api.get("/doctors", { params: { city: "pune", featured: true, limit: 6 } }).then((r) => setFeatured(r.data.items || []));
    api.get("/doctors", { params: { city: "pune", limit: 8 } }).then((r) => setRecent(r.data.items || []));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (specialty) params.set("specialty", specialty);
    const base = area ? `/doctors/pune/area/${area}` : "/doctors/pune";
    navigate(`${base}?${params.toString()}`);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "LocalDoctor.in",
    url: "https://localdoctor.in",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://localdoctor.in/doctors/pune?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <>
      <Seo
        title="Find trusted doctors near you"
        description={settings.meta_description}
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative hero-grid">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 pt-14 lg:pt-20 pb-10 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent text-[hsl(var(--ld-jade-fg))] px-3 py-1 text-xs font-semibold">
              <BadgeCheck size={14} /> Verified clinics across Pune
            </span>
            <h1 className="mt-5 font-display text-balance text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              {settings.tagline.includes("…") ? (
                <>
                  {settings.tagline.split("…")[0]}…
                  <span className="block text-primary">{settings.tagline.split("…")[1]?.trim()}</span>
                </>
              ) : settings.tagline}
            </h1>
            <p className="mt-5 text-[17px] text-muted-foreground max-w-xl text-pretty">
              {settings.hero_subtitle}
            </p>

            <form onSubmit={onSearch} className="mt-7 bg-white rounded-2xl border border-border shadow-[0_10px_40px_-20px_rgba(0,0,0,0.15)] p-3 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid="hero-search-form">
              <div className="md:col-span-5 flex items-center gap-2 rounded-lg bg-muted/60 px-3">
                <Search size={18} className="text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search doctor, specialty or clinic"
                  className="w-full bg-transparent py-3 outline-none text-[15px]"
                  data-testid="hero-search-input"
                />
              </div>
              <div className="md:col-span-3 flex items-center gap-2 rounded-lg bg-muted/60 px-3">
                <MapPin size={18} className="text-muted-foreground" />
                <select
                  value="pune"
                  onChange={() => {}}
                  className="w-full bg-transparent py-3 outline-none text-[15px]"
                  data-testid="hero-city-select"
                >
                  <option value="pune">Pune</option>
                </select>
              </div>
              <div className="md:col-span-3 flex items-center gap-2 rounded-lg bg-muted/60 px-3">
                <select
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  className="w-full bg-transparent py-3 outline-none text-[15px]"
                  data-testid="hero-area-select"
                >
                  <option value="">All areas</option>
                  {areas.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                </select>
              </div>
              <button type="submit" className="btn-primary md:col-span-1 w-full" data-testid="hero-search-submit">
                Search
              </button>

              <div className="md:col-span-12 flex flex-wrap items-center gap-2 pt-2">
                <span className="text-xs text-muted-foreground pr-1">Popular:</span>
                {specialties.slice(0, 6).map((s) => (
                  <button
                    key={s.slug}
                    type="button"
                    onClick={() => { setSpecialty(s.slug); navigate(`/doctors/pune/${s.slug}`); }}
                    className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-muted"
                    data-testid={`popular-chip-${s.slug}`}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            </form>

            <div className="mt-6 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-primary" /> Admin-verified doctors</span>
              <span className="inline-flex items-center gap-2"><Timer size={16} className="text-primary" /> Skip-the-queue OTP</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-xl aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/5888168/pexels-photo-5888168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=700"
                alt="Doctor consulting a patient"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
            <div className="absolute -left-4 -bottom-4 bg-white rounded-2xl border border-border shadow-xl p-4 flex items-center gap-3 max-w-[260px]">
              <div className="h-10 w-10 rounded-full bg-accent text-[hsl(var(--ld-jade-fg))] grid place-items-center">
                <Activity size={18} />
              </div>
              <div>
                <p className="text-sm font-semibold">Live queue updates</p>
                <p className="text-xs text-muted-foreground">See your slot time and expected wait</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POPULAR SPECIALTIES */}
      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-14">
        <div className="flex items-end justify-between gap-6 mb-6">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold">Browse by specialty</h2>
            <p className="text-muted-foreground mt-1">Find the right expert for your concern.</p>
          </div>
          <Link to="/specialties" className="text-sm font-semibold text-primary hover:underline hidden md:inline-flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar md:grid md:grid-cols-5 lg:grid-cols-10">
          {specialties.map((s) => {
            const Icon = SPEC_ICON[s.slug] || Stethoscope;
            return (
              <Link
                key={s.slug}
                to={`/doctors/pune/${s.slug}`}
                className="min-w-[140px] rounded-xl border border-border p-4 flex flex-col items-start gap-3 bg-card card-hover"
                data-testid={`specialty-tile-${s.slug}`}
              >
                <span className="h-10 w-10 rounded-lg bg-accent text-[hsl(var(--ld-jade-fg))] grid place-items-center">
                  <Icon size={20} />
                </span>
                <span className="font-semibold text-sm leading-tight">{s.name}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED DOCTORS */}
      <section className="bg-secondary/50 border-y border-border">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-14">
          <div className="flex items-end justify-between gap-6 mb-6">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-bold">Popular doctors in Pune</h2>
              <p className="text-muted-foreground mt-1">Trusted practitioners the community keeps coming back to.</p>
            </div>
            <Link to="/doctors/pune" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
              See all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((d) => <DoctorCard key={d.id} doctor={d} />)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-16 grid lg:grid-cols-12 gap-10 items-center" id="how">
        <div className="lg:col-span-5">
          <h2 className="font-display text-3xl md:text-4xl font-bold">How Local Doctor works</h2>
          <p className="mt-4 text-muted-foreground max-w-md">
            A clinic visit that respects your time. Three simple steps and you're seen without
            waiting in line.
          </p>
          <Link to="/doctors/pune" className="btn-primary mt-6">Find a doctor now <ArrowRight size={16} /></Link>
        </div>
        <ol className="lg:col-span-7 grid gap-4">
          {[
            { icon: Search, title: "Search", desc: "Pick your specialty and area, or search by doctor name." },
            { icon: BadgeCheck, title: "Book your slot", desc: "Choose a time from the doctor's live schedule." },
            { icon: Timer, title: "Walk in with your OTP", desc: "Skip the queue — show the OTP at the clinic and be seen on time." },
          ].map((step) => {
            const Icon = step.icon;
            return (
              <li key={step.title} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5">
                <span className="h-11 w-11 rounded-xl bg-accent text-[hsl(var(--ld-jade-fg))] grid place-items-center shrink-0">
                  <Icon size={22} />
                </span>
                <div>
                  <h3 className="font-display font-semibold">{step.title}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{step.desc}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </section>

      {/* WHY LOCAL DOCTOR */}
      <section className="bg-[hsl(var(--ld-hero))] border-y border-border">
        <div className="mx-auto max-w-[1280px] px-4 lg:px-6 py-16 grid md:grid-cols-3 gap-6">
          {[
            { title: "Verified by our team", desc: "We review registration numbers and clinic details before marking a doctor Verified.", icon: ShieldCheck },
            { title: "Transparent fees & timings", desc: "See consultation fee, languages and timings up front — no surprises.", icon: BadgeCheck },
            { title: "Direct, human contact", desc: "Call, WhatsApp or send a message. Your details stay with the clinic.", icon: MessageSquare },
          ].map((c) => {
            const I = c.icon;
            return (
              <article key={c.title} className="rounded-xl bg-white border border-border p-6">
                <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                  <I size={20} />
                </span>
                <h3 className="mt-4 font-display font-semibold text-lg">{c.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{c.desc}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* RECENTLY ADDED */}
      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 py-16">
        <div className="flex items-end justify-between gap-6 mb-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold">Recently added in Pune</h2>
          <Link to="/doctors/pune" className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
            Browse all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {recent.map((d) => <DoctorCard key={d.id} doctor={d} compact />)}
        </div>
      </section>

      {/* FOR DOCTORS CTA */}
      <section className="mx-auto max-w-[1280px] px-4 lg:px-6 pb-16">
        <div className="rounded-3xl bg-[hsl(var(--ld-footer))] text-white p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold">Are you a doctor? Grow your practice.</h2>
            <p className="mt-3 text-white/70 max-w-lg">
              List your clinic on LocalDoctor.in and start accepting queue-less OTP appointments from
              verified patients near you.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/list-your-practice" className="btn-primary" data-testid="cta-list-practice">List your practice</Link>
            <Link to="/contact" className="btn-secondary bg-white/10 text-white border-white/20 hover:bg-white/20">
              <PhoneCall size={16} /> Talk to us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
